import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PublicLayout } from '@/components/PublicLayout';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Brain, BarChart3, FileText, Building2, Shield, FileCheck } from 'lucide-react';

const features = [
  {
    icon: Brain,
    title: 'Analisi AI con Claude',
    description: 'Intelligenza artificiale avanzata estrae automaticamente tutte le voci di spesa dai PDF e le categorizza con precisione professionale.',
  },
  {
    icon: BarChart3,
    title: 'Grafici Interattivi',
    description: 'Visualizza i dati con grafici a torta, barre e scostamenti. Confronta consuntivo vs preventivo con chiarezza immediata.',
  },
  {
    icon: FileCheck,
    title: 'Nota di Sintesi',
    description: "Genera automaticamente la nota sintetica esplicativa conforme all'art. 1130 c.c. con analisi dettagliata degli scostamenti.",
  },
  {
    icon: FileText,
    title: 'Gestione Documenti',
    description: 'Carica e organizza consuntivi e preventivi in formato PDF. Storico completo per ogni condominio e anno.',
  },
  {
    icon: Building2,
    title: 'Multi-Condominio',
    description: 'Gestisci più condomini dalla stessa piattaforma. Ogni condominio con i suoi documenti e analisi separate.',
  },
  {
    icon: Shield,
    title: 'Sicurezza',
    description: 'Dati protetti con crittografia avanzata. Accesso riservato solo agli amministratori autorizzati.',
  },
];

const steps = [
  {
    number: '01',
    title: 'Carica PDF',
    description: 'Carica il documento PDF del consuntivo o preventivo. Supporto per documenti multi-pagina e formati standard.',
  },
  {
    number: '02',
    title: 'Analisi AI',
    description: "L'intelligenza artificiale analizza il documento, estrae tutte le voci di spesa e le categorizza automaticamente.",
  },
  {
    number: '03',
    title: 'Report Completo',
    description: 'Visualizza grafici interattivi, tabelle dettagliate e nota di sintesi professionale. Esporta in PDF quando necessario.',
  },
];

export default function Home() {
  return (
    <PublicLayout>
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.CONDO_HERO_3}
            alt="Condominio moderno"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="mb-8 flex justify-center">
              <img
                src={IMAGES.LOGO_EGIM_25}
                alt="EGIM Logo"
                className="h-28 w-auto object-contain drop-shadow-lg"
              />
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
              EgimConsuntivi
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-12 max-w-3xl mx-auto">
              Analisi Professionale dei Condomini con intelligenza artificiale. Trasforma i PDF di consuntivi e preventivi in report completi con grafici interattivi e note di sintesi automatiche.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8 py-6">
                <Link to={ROUTE_PATHS.LOGIN}>Accedi alla Piattaforma</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="features" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Funzionalità Complete</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Tutto ciò di cui hai bisogno per gestire i consuntivi condominiali in modo professionale
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <CardTitle className="text-xl">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base">{feature.description}</CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Come Funziona</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Tre semplici passaggi per ottenere analisi professionali dei tuoi consuntivi
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-12 max-w-5xl mx-auto">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="text-center"
              >
                <div className="mb-6 flex justify-center">
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-3xl font-bold text-primary">{step.number}</span>
                  </div>
                </div>
                <h3 className="text-2xl font-semibold mb-4">{step.title}</h3>
                <p className="text-muted-foreground">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="cta" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Inizia Oggi Stesso</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Accedi alla piattaforma riservata agli amministratori autorizzati
            </p>
            <Button asChild size="lg" className="text-lg px-8 py-6">
              <Link to={ROUTE_PATHS.LOGIN}>Accedi alla Piattaforma</Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-6">
              Nota: L'accesso è riservato agli account creati dall'amministratore di sistema
            </p>
          </motion.div>
        </div>
      </section>
    </PublicLayout>
  );
}